import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, MenuItem, Tabs, Tab,
  CircularProgress, Avatar, Chip, IconButton, Tooltip, Stack, Divider, Alert, Paper,
  List, ListItemButton, ListItemAvatar, ListItemText, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { taskAPI, userAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';

const STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const ROLE_COLORS = { admin: '#C62828', support_team: '#1565C0', qa_team: '#6A1B9A', team_member: '#2E7D32' };

const fmtTime = (d) => {
  try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; }
};

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function TaskDetailPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAdmin, isSupportTeam } = useAuth();
  const { showToast } = useToast();
  const canManage = isAdmin() || isSupportTeam();
  const tabNames = ['details', 'members', 'remarks', 'communication'];
  const [tab, setTab] = useState(Math.max(0, tabNames.indexOf(searchParams.get('tab'))));

  const [form, setForm] = useState({ title: '', description: '', start_date: '', end_date: '', tat_date: '', status: 'planning' });
  const [members, setMembers] = useState([]);
  const [fetching, setFetching] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const endRef = useRef(null);

  const loadTask = useCallback(() => {
    if (isNew) return;
    taskAPI.getOne(id)
      .then((res) => {
        const t = res.data;
        setForm({
          title: t.title || '', description: t.description || '', start_date: t.start_date || '',
          end_date: t.end_date || '', tat_date: t.tat_date || '', status: t.status || 'planning',
        });
        setMembers(t.members || []);
      })
      .catch((err) => { if (err.response?.status === 403 || err.response?.status === 404) setDenied(true); else showToast('Failed to load task', 'error'); })
      .finally(() => setFetching(false));
    // eslint-disable-next-line
  }, [id]);

  const loadRemarks = useCallback(() => {
    if (isNew) return;
    taskAPI.getRemarks(id).then((res) => setRemarks(res.data)).catch(() => {});
  }, [id, isNew]);

  const loadMessages = useCallback(() => {
    if (isNew) return;
    taskAPI.getMessages(id).then((res) => setMessages(res.data)).catch(() => {});
  }, [id, isNew]);

  useEffect(() => {
    loadTask(); loadRemarks(); loadMessages();
    if (canManage) userAPI.getAll().then((res) => setAllUsers(res.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  // Poll communication so everyone sees new messages without refreshing.
  useEffect(() => {
    if (isNew) return undefined;
    const t = setInterval(loadMessages, 8000);
    return () => clearInterval(t);
  }, [isNew, loadMessages]);

  useEffect(() => {
    if (tab === 3 && endRef.current) endRef.current.scrollIntoView({ block: 'nearest' });
  }, [messages, tab]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return showToast('Task name is required', 'error');
    setSaving(true);
    try {
      if (isNew) {
        const res = await taskAPI.create(form);
        showToast('Task created! Now add team members.', 'success');
        navigate(`/tasks/${res.data.id}?tab=members`, { replace: true });
      } else {
        await taskAPI.update(id, form);
        showToast('Task updated', 'success');
        loadTask();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save task', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task permanently?')) return;
    try {
      await taskAPI.remove(id);
      showToast('Task deleted', 'success');
      navigate('/tasks');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete task', 'error');
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;
    try {
      await taskAPI.addMember(id, { user_id: selectedUser.id });
      setSelectedUser(null);
      loadTask();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the task?')) return;
    try {
      await taskAPI.removeMember(id, userId);
      loadTask();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove member', 'error');
    }
  };

  const handleAddRemark = async () => {
    const text = newRemark.trim();
    if (!text) return;
    setRemarkSaving(true);
    try {
      await taskAPI.addRemark(id, { remark: text });
      setNewRemark('');
      loadRemarks();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add remark', 'error');
    } finally {
      setRemarkSaving(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm('Delete this remark?')) return;
    try { await taskAPI.deleteRemark(id, remarkId); loadRemarks(); } catch (err) { showToast(err.response?.data?.message || 'Failed to delete remark', 'error'); }
  };

  // ── Communication with @mention suggestions ──
  const handleMessageChange = (e) => {
    const v = e.target.value;
    setNewMessage(v);
    const m = v.match(/(?:^|\s)@([^\s@]*)$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const mentionCandidates = mentionQuery === null ? [] : members
    .filter((m) => m.user_id !== user?.id && m.member?.name && m.member.name.toLowerCase().includes(mentionQuery))
    .slice(0, 6);

  const selectMention = (name) => {
    setNewMessage((prev) => prev.replace(/@([^\s@]*)$/, '@' + name + ' '));
    setMentionQuery(null);
  };

  const handleSendMessage = async () => {
    const text = newMessage.trim();
    if (!text) return;
    setSending(true);
    try {
      const mentions = members
        .filter((m) => m.member?.name && text.includes('@' + m.member.name))
        .map((m) => m.user_id);
      await taskAPI.addMessage(id, { message: text, mentions });
      setNewMessage('');
      setMentionQuery(null);
      loadMessages();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try { await taskAPI.deleteMessage(id, messageId); loadMessages(); } catch (err) { showToast(err.response?.data?.message || 'Failed to delete message', 'error'); }
  };

  if (fetching) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  if (denied) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tasks')} sx={{ mb: 2 }}>Back to Tasks</Button>
        <Alert severity="warning">This task was not found, or you are not a member of it.</Alert>
      </Box>
    );
  }

  const memberIds = members.map((m) => m.user_id);
  const addableUsers = allUsers.filter((u) => !memberIds.includes(u.id) && u.status !== 'inactive');
  const fieldLocked = !isNew && !canManage; // members can only change status, end date and TAT date

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <IconButton onClick={() => navigate('/tasks')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }} noWrap>{isNew ? 'New Task' : (form.title || 'Task')}</Typography>
          <Typography variant="body2" color="text.secondary">In-house task</Typography>
        </Box>
        {!isNew && isAdmin() && (
          <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={handleDelete}>Delete</Button>
        )}
      </Box>

      <Card>
        {!isNew && (
          <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              <Tab label="Details" />
              <Tab label={`Team Members (${members.length})`} />
              <Tab label={`Remarks (${remarks.length})`} />
              <Tab label={`Communication (${messages.length})`} />
            </Tabs>
          </Box>
        )}
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* ── Details ── */}
          <TabPanel value={isNew ? 0 : tab} index={0}>
            <Box component="form" onSubmit={handleSave}>
              {fieldLocked && <Alert severity="info" sx={{ mb: 2 }}>You can update the Status, End Date and TAT Date. Other fields are managed by admin / support.</Alert>}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth required label="Task Name" value={form.title} disabled={fieldLocked}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={4} label="Details" value={form.description} disabled={fieldLocked}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Start Date" type="date" value={form.start_date} disabled={fieldLocked}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="End Date" type="date" value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="TAT Date" type="date" value={form.tat_date}
                    onChange={(e) => setForm({ ...form, tat_date: e.target.value })} InputLabelProps={{ shrink: true }}
                    helperText="Any team member can update this" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="Status" value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.filter((s) => canManage || !['completed', 'cancelled'].includes(s) || s === form.status).map((s) => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button variant="outlined" onClick={() => navigate('/tasks')} disabled={saving}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={saving}
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}>
                  {isNew ? 'Create Task' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* ── Team members ── */}
          <TabPanel value={tab} index={1}>
            {canManage && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
                <Autocomplete fullWidth size="small" options={addableUsers} value={selectedUser}
                  getOptionLabel={(u) => `${u.name} (${(u.role || '').replace('_', ' ')})`}
                  onChange={(_, v) => setSelectedUser(v)}
                  renderInput={(params) => <TextField {...params} label="Add team member" />} />
                <Button variant="contained" startIcon={<PersonAddIcon />} disabled={!selectedUser} onClick={handleAddMember} sx={{ whiteSpace: 'nowrap' }}>
                  Add Member
                </Button>
              </Stack>
            )}
            {members.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No members assigned yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {members.map((m) => (
                  <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700, bgcolor: (ROLE_COLORS[m.member?.role] || '#888') + '30', color: ROLE_COLORS[m.member?.role] || '#888' }}>
                      {m.member?.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.member?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{m.member?.email} · {(m.member?.role || '').replace('_', ' ')}</Typography>
                    </Box>
                    {canManage && (
                      <Tooltip title="Remove from task">
                        <IconButton size="small" onClick={() => handleRemoveMember(m.user_id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* ── Remarks ── */}
          <TabPanel value={tab} index={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
              <TextField fullWidth multiline minRows={2} maxRows={6} size="small" placeholder="Write a remark for this task…"
                value={newRemark} onChange={(e) => setNewRemark(e.target.value)} />
              <Button variant="contained" onClick={handleAddRemark} disabled={remarkSaving || !newRemark.trim()} startIcon={<SendIcon />} sx={{ whiteSpace: 'nowrap', mt: 0.3 }}>
                Add Remark
              </Button>
            </Stack>
            {remarks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No remarks yet.</Typography>
            ) : (
              <Stack spacing={1.2}>
                {remarks.map((r) => (
                  <Box key={r.id} sx={{ display: 'flex', gap: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700 }}>{r.author?.name?.charAt(0)}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary"><b>{r.author?.name || 'User'}</b> · {fmtTime(r.created_at)}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.remark}</Typography>
                    </Box>
                    {(r.user_id === user?.id || isAdmin()) && (
                      <Tooltip title="Delete remark"><IconButton size="small" onClick={() => handleDeleteRemark(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* ── Communication ── */}
          <TabPanel value={tab} index={3}>
            <Box sx={{ maxHeight: 420, overflowY: 'auto', mb: 2, pr: 0.5 }}>
              {messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">No messages yet. Start the conversation.</Typography>
                </Box>
              ) : (
                <Stack spacing={1.2}>
                  {messages.map((m) => {
                    const mine = m.user_id === user?.id;
                    return (
                      <Box key={m.id} sx={{ display: 'flex', gap: 1.2, flexDirection: mine ? 'row-reverse' : 'row' }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700, bgcolor: (ROLE_COLORS[m.author?.role] || '#888') + '30', color: ROLE_COLORS[m.author?.role] || '#888' }}>
                          {m.author?.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ maxWidth: '78%', p: 1.2, borderRadius: 2, bgcolor: mine ? 'primary.main' : 'action.hover', color: mine ? 'primary.contrastText' : 'text.primary' }}>
                          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                            <b>{m.author?.name}</b> · {fmtTime(m.created_at)}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</Typography>
                          {(mine || canManage) && (
                            <Typography variant="caption" onClick={() => handleDeleteMessage(m.id)} sx={{ cursor: 'pointer', opacity: 0.7, textDecoration: 'underline' }}>
                              delete
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={endRef} />
                </Stack>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ position: 'relative' }}>
              {mentionCandidates.length > 0 && (
                <Paper elevation={4} sx={{ position: 'absolute', bottom: '100%', left: 0, mb: 0.5, zIndex: 10, minWidth: 240 }}>
                  <List dense disablePadding>
                    {mentionCandidates.map((m) => (
                      <ListItemButton key={m.user_id} onClick={() => selectMention(m.member.name)}>
                        <ListItemAvatar sx={{ minWidth: 36 }}><Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{m.member.name.charAt(0)}</Avatar></ListItemAvatar>
                        <ListItemText primary={m.member.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
              <Stack direction="row" spacing={1.5}>
                <TextField fullWidth size="small" placeholder="Write a message to the task team… (@ to mention)" value={newMessage}
                  onChange={handleMessageChange}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }} />
                <Button variant="contained" onClick={handleSendMessage} disabled={sending || !newMessage.trim()} endIcon={<SendIcon />}>Send</Button>
              </Stack>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
      {isNew && <Chip label="After creating the task you can add team members, remarks and messages." size="small" sx={{ mt: 2 }} />}
    </Box>
  );
}